import { useReducer, useCallback, useRef, useEffect } from 'react';
import { TopDownItemId } from './config';
import type { Position } from './positions';
import { computeScatterPositions } from './positions';
import type { ItemScatterConfig } from './scatterConfig';
import type { ItemFocusedPoseConfig } from './focusConfig';
import type { TopDownItemConfig } from './config';

export const SCATTER_MS = 420;
export const CONTENT_FADE_MS = 600;

export type FocusPhase = 'idle' | 'focusing' | 'focused' | 'unfocusing';

export interface FocusState {
    phase: FocusPhase;
    focusedId: TopDownItemId | null;
    mode: 'generic' | 'self';
    overlayMounted: boolean;
    overlayVisible: boolean;
    contentId?: string;
}

type FocusAction =
    | { type: 'FOCUS_START'; id: TopDownItemId; config: ItemScatterConfig }
    | { type: 'FOCUS_SCATTER_COMPLETE' }
    | { type: 'FOCUS_CONTENT_MOUNTED' }
    | { type: 'DISMISS_START' }
    | { type: 'DISMISS_CONTENT_UNMOUNTED' }
    | { type: 'DISMISS_UNSCATTER_COMPLETE' };

function focusReducer(state: FocusState, action: FocusAction): FocusState {
    switch (action.type) {
        case 'FOCUS_START':
            return {
                phase: 'focusing',
                focusedId: action.id,
                mode: action.config.mode,
                overlayMounted: false,
                overlayVisible: false,
                contentId: action.config.contentId,
            };
        case 'FOCUS_SCATTER_COMPLETE':
            return {
                ...state,
                overlayMounted: true,
            };
        case 'FOCUS_CONTENT_MOUNTED':
            return {
                ...state,
                phase: 'focused',
                overlayVisible: true,
            };
        case 'DISMISS_START':
            return {
                ...state,
                phase: 'unfocusing',
                overlayVisible: false,
            };
        case 'DISMISS_CONTENT_UNMOUNTED':
            return {
                ...state,
                overlayMounted: false,
            };
        case 'DISMISS_UNSCATTER_COMPLETE':
            return {
                phase: 'idle',
                focusedId: null,
                mode: 'generic',
                overlayMounted: false,
                overlayVisible: false,
            };
        default:
            return state;
    }
}

export interface UseFocusControllerOptions {
    itemsConfig: Map<TopDownItemId, TopDownItemConfig>;
    scatterConfig: Map<TopDownItemId, ItemScatterConfig>;
    focusConfig: Map<TopDownItemId, ItemFocusedPoseConfig>;
}

export interface UseFocusControllerReturn {
    state: FocusState;
    focusItem: (id: TopDownItemId) => void;
    dismiss: () => void;
    getItemPosition: (id: TopDownItemId) => Position;
    getTargetPose: (
        id: TopDownItemId
    ) => { top: string; left: string; width: string; rotate: string; z: number };
    isItemFocused: (id: TopDownItemId) => boolean;
    isAnyFocus: () => boolean;
    canInteractWithItems: () => boolean;
    onContentExitComplete: () => void;
}

/**
 * Reducer-based controller for managing focus/scatter animations
 */
export function useFocusController({
    itemsConfig,
    scatterConfig,
    focusConfig,
}: UseFocusControllerOptions): UseFocusControllerReturn {
    const [state, dispatch] = useReducer(focusReducer, {
        phase: 'idle',
        focusedId: null,
        mode: 'generic',
        overlayMounted: false,
        overlayVisible: false,
    });

    // Track current state in ref to avoid stale closures
    const stateRef = useRef(state);
    useEffect(() => {
        const timestamp = performance.now();
        const prevPhase = stateRef.current.phase;
        stateRef.current = state;
        
        // Sync pending focus ref with actual state (only if not manually set)
        // Don't override if we're in the middle of a delayed update
        if (state.phase === pendingFocusRef.current.phase || 
            (state.phase === 'idle' && pendingFocusRef.current.phase === 'idle')) {
            pendingFocusRef.current = {
                phase: state.phase,
                focusedId: state.focusedId,
            };
        }
        
        // Debug logging to track state transitions
        if (process.env.NODE_ENV === 'development') {
            console.log(`[State] TRANSITION`, {
                timestamp: timestamp.toFixed(2),
                from: prevPhase,
                to: state.phase,
                focusedId: state.focusedId,
                mode: state.mode,
            });
        }
    }, [state]);

    // Store original positions for restoration
    const originalPositionsRef = useRef<Map<TopDownItemId, Position>>(new Map());
    const scatterPositionsRef = useRef<Map<TopDownItemId, Position>>(new Map());
    
    // Track pending focus state synchronously
    // This allows getItemPosition to return new positions immediately,
    // even before React state updates (which are batched)
    const pendingFocusRef = useRef<{
        phase: FocusPhase;
        focusedId: TopDownItemId | null;
    }>({
        phase: 'idle',
        focusedId: null,
    });

    // Initialize original positions from config (synchronously)
    if (originalPositionsRef.current.size === 0) {
        itemsConfig.forEach((config, id) => {
            originalPositionsRef.current.set(id, {
                top: config.top,
                left: config.left,
            });
        });
    }

    // Update positions when config changes
    useEffect(() => {
        itemsConfig.forEach((config, id) => {
            originalPositionsRef.current.set(id, {
                top: config.top,
                left: config.left,
            });
        });
    }, [itemsConfig]);

    // Timer refs for cleanup
    const scatterTimerRef = useRef<number | null>(null);
    const contentMountTimerRef = useRef<number | null>(null);
    
    // Flag to prevent double-dismiss calls
    const isDismissingRef = useRef<boolean>(false);
    // Queue for next focus request while a transition/dismiss is in progress
    const queuedFocusIdRef = useRef<TopDownItemId | null>(null);

    // Cleanup timers on unmount
    useEffect(() => {
        return () => {
            if (scatterTimerRef.current) clearTimeout(scatterTimerRef.current);
            if (contentMountTimerRef.current) clearTimeout(contentMountTimerRef.current);
        };
    }, []);

    /**
     * Complete the dismiss sequence by resetting scatter positions and transitioning to idle
     * Used for both generic mode (after content fade) and self-focus mode (directly after RAF delays)
     */
    const completeDismissSequence = useCallback(() => {
        // For generic mode, dispatch content unmounted first
        // For self-focus mode, this is a no-op since overlayMounted is already false
        dispatch({ type: 'DISMISS_CONTENT_UNMOUNTED' });

        // Use requestAnimationFrame to ensure layout is stable before resetting positions
        const rafTimestamp = performance.now();
        requestAnimationFrame(() => {
            const raf1Time = performance.now();
            if (process.env.NODE_ENV === 'development') {
                console.log(`[Timer] RAF (scatter reset 1)`, {
                    timestamp: raf1Time.toFixed(2),
                    elapsed: (raf1Time - rafTimestamp).toFixed(2) + 'ms',
                });
            }
            // Additional frame to ensure layout is fully stable
            requestAnimationFrame(() => {
                const raf2Time = performance.now();
                const scatterResetTime = performance.now();
                // Un-scatter items back to original positions
                scatterPositionsRef.current = originalPositionsRef.current;
                
                if (process.env.NODE_ENV === 'development') {
                    console.log(`[Timer] RAF (scatter reset 2)`, {
                        timestamp: raf2Time.toFixed(2),
                        elapsed: (raf2Time - rafTimestamp).toFixed(2) + 'ms',
                    });
                    const schedulePos = scatterPositionsRef.current.get(TopDownItemId.Schedule);
                    console.log(`[State] SCATTER_POS reset (dismiss)`, {
                        timestamp: scatterResetTime.toFixed(2),
                        schedulePos: schedulePos ? `${schedulePos.top}, ${schedulePos.left}` : 'N/A',
                    });
                }

                // After un-scatter completes, return to idle
                // This is critical - must reset to idle to allow new actions
                const unscatterTimerTimestamp = performance.now();
                if (process.env.NODE_ENV === 'development') {
                    console.log(`[Timer] SET unscatter complete`, {
                        timestamp: unscatterTimerTimestamp.toFixed(2),
                        delay: SCATTER_MS + 'ms',
                    });
                }
                scatterTimerRef.current = window.setTimeout(() => {
                    const unscatterFireTime = performance.now();
                    if (process.env.NODE_ENV === 'development') {
                        console.log(`[Timer] FIRE unscatter complete`, {
                            timestamp: unscatterFireTime.toFixed(2),
                            elapsed: (unscatterFireTime - unscatterTimerTimestamp).toFixed(2) + 'ms',
                        });
                    }
                    // Update pending focus ref synchronously
                    pendingFocusRef.current = {
                        phase: 'idle',
                        focusedId: null,
                    };
                    // Reset dismiss flag to allow future dismiss calls
                    isDismissingRef.current = false;
                    dispatch({ type: 'DISMISS_UNSCATTER_COMPLETE' });
                    // Verify state is reset
                    if (process.env.NODE_ENV === 'development') {
                        console.log('[FocusController] Dismiss complete, should be idle');
                    }
                    // If a focus was queued, trigger it now
                    const nextId = queuedFocusIdRef.current;
                    queuedFocusIdRef.current = null;
                    if (nextId) {
                        if (process.env.NODE_ENV === 'development') {
                            console.log('[FocusController] Processing queued focus after dismiss', { nextId });
                        }
                        // Defer to next tick to allow React state to settle
                        setTimeout(() => {
                            focusItem(nextId);
                        }, 0);
                    }
                }, SCATTER_MS);
            });
        });
    }, []);

    const dismiss = useCallback(() => {
        const timestamp = performance.now();
        // Check current state from ref to avoid stale closures
        const currentState = stateRef.current;
        
        if (process.env.NODE_ENV === 'development') {
            console.log(`[Action] dismiss() called`, {
                timestamp: timestamp.toFixed(2),
                currentPhase: currentState.phase,
                focusedId: currentState.focusedId,
                isDismissing: isDismissingRef.current,
            });
        }
        
        // Ignore if not focused or already dismissing
        if (currentState.phase !== 'focused' || isDismissingRef.current) {
            if (process.env.NODE_ENV === 'development') {
                console.log('[FocusController] Blocked dismiss - phase:', currentState.phase, 'isDismissing:', isDismissingRef.current);
            }
            return;
        }

        // Set flag to prevent double-dismiss
        isDismissingRef.current = true;

        // Clear any pending timers
        if (scatterTimerRef.current) clearTimeout(scatterTimerRef.current);
        if (contentMountTimerRef.current) clearTimeout(contentMountTimerRef.current);

        // Start dismiss sequence: fade out content first
        dispatch({ type: 'DISMISS_START' });

        // Delay pendingFocusRef update to next frame to ensure state is consistent
        const rafTimestamp = performance.now();
        requestAnimationFrame(() => {
            const raf1Time = performance.now();
            if (process.env.NODE_ENV === 'development') {
                console.log(`[Timer] RAF (dismiss pendingRef)`, {
                    timestamp: raf1Time.toFixed(2),
                    elapsed: (raf1Time - rafTimestamp).toFixed(2) + 'ms',
                });
            }
            // Additional frame for layout stability
            requestAnimationFrame(() => {
                const raf2Time = performance.now();
                const pendingTimestamp = performance.now();
                pendingFocusRef.current = {
                    phase: 'unfocusing',
                    focusedId: null,
                };
                if (process.env.NODE_ENV === 'development') {
                    console.log(`[Timer] RAF (dismiss pendingRef 2)`, {
                        timestamp: raf2Time.toFixed(2),
                        elapsed: (raf2Time - rafTimestamp).toFixed(2) + 'ms',
                    });
                    console.log(`[State] PENDING_REF updated (dismiss)`, {
                        timestamp: pendingTimestamp.toFixed(2),
                        phase: 'unfocusing',
                        focusedId: null,
                    });
                }

                // For self-focus mode, complete dismiss sequence directly (no content modal to wait for)
                // For generic mode, onContentExitComplete will handle this after content fades out
                if (currentState.mode === 'self') {
                    if (process.env.NODE_ENV === 'development') {
                        console.log('[FocusController] Self-focus mode detected, completing dismiss directly');
                    }
                    completeDismissSequence();
                }
            });
        });
    }, [completeDismissSequence]);

    const focusItem = useCallback((id: TopDownItemId) => {
        const timestamp = performance.now();
        // Check current state from ref to avoid stale closures
        const currentState = stateRef.current;
        
        if (process.env.NODE_ENV === 'development') {
            console.log(`[Action] focusItem() called`, {
                timestamp: timestamp.toFixed(2),
                itemId: id,
                currentPhase: currentState.phase,
                currentFocusedId: currentState.focusedId,
            });
        }
        
        // Handle interactions during active transitions by queueing next focus
        if (currentState.phase === 'focusing' || currentState.phase === 'unfocusing') {
            queuedFocusIdRef.current = id;
            if (process.env.NODE_ENV === 'development') {
                console.log('[FocusController] Queued focus during active transition:', { nextId: id, phase: currentState.phase });
            }
            return;
        }

        // If focused, either dismiss same item or queue-and-dismiss for different item
        if (currentState.phase === 'focused') {
            if (currentState.focusedId === id) {
                dismiss();
                return;
            }
            // Switch focus: queue next and dismiss first
            queuedFocusIdRef.current = id;
            if (process.env.NODE_ENV === 'development') {
                console.log('[FocusController] Switching focus: queue next and dismiss current', { nextId: id, currentFocusedId: currentState.focusedId });
            }
            dismiss();
            return;
        }

        const config = scatterConfig.get(id);
        if (!config) return;

        // Clear any pending timers
        if (scatterTimerRef.current) clearTimeout(scatterTimerRef.current);
        if (contentMountTimerRef.current) clearTimeout(contentMountTimerRef.current);

        // Compute scatter positions BEFORE dispatching to ensure positions are ready
        // This must happen synchronously so Framer Motion animates to correct positions
        const scatterTimestamp = performance.now();
        scatterPositionsRef.current = computeScatterPositions(
            originalPositionsRef.current,
            config.mode === 'self' ? id : null
        );
        
        if (process.env.NODE_ENV === 'development') {
            const schedulePos = scatterPositionsRef.current.get(TopDownItemId.Schedule);
            console.log(`[State] SCATTER_POS updated (focus)`, {
                timestamp: scatterTimestamp.toFixed(2),
                itemId: id,
                schedulePos: schedulePos ? `${schedulePos.top}, ${schedulePos.left}` : 'N/A',
            });
        }

        // Update pending focus ref synchronously BEFORE dispatch
        // This allows getItemPosition to return new positions immediately,
        // even before React state updates
        const pendingTimestamp = performance.now();
        pendingFocusRef.current = {
            phase: 'focusing',
            focusedId: id,
        };
        
        if (process.env.NODE_ENV === 'development') {
            console.log(`[State] PENDING_REF updated (focus)`, {
                timestamp: pendingTimestamp.toFixed(2),
                phase: 'focusing',
                focusedId: id,
            });
        }

        // Start focus sequence
        dispatch({ type: 'FOCUS_START', id, config });

        // After scatter completes, mount content
        const timerTimestamp = performance.now();
        if (process.env.NODE_ENV === 'development') {
            console.log(`[Timer] SET scatter complete`, {
                timestamp: timerTimestamp.toFixed(2),
                delay: SCATTER_MS + 'ms',
            });
        }
        scatterTimerRef.current = window.setTimeout(() => {
            const fireTime = performance.now();
            if (process.env.NODE_ENV === 'development') {
                console.log(`[Timer] FIRE scatter complete`, {
                    timestamp: fireTime.toFixed(2),
                    elapsed: (fireTime - timerTimestamp).toFixed(2) + 'ms',
                });
            }
            dispatch({ type: 'FOCUS_SCATTER_COMPLETE' });
            dispatch({ type: 'FOCUS_CONTENT_MOUNTED' });
        }, SCATTER_MS);
    }, [scatterConfig, dismiss]);


    const onContentExitComplete = useCallback(() => {
        // Exit animation is complete at this point — proceed immediately
        // Unmount content and continue dismiss sequence to avoid any flash
        if (process.env.NODE_ENV === 'development') {
            console.log('[FocusController] onContentExitComplete → completing dismiss sequence immediately');
        }
        completeDismissSequence();
    }, [completeDismissSequence]);

    const getItemPosition = useCallback((id: TopDownItemId): Position => {
        const timestamp = performance.now();
        const original = originalPositionsRef.current.get(id);
        if (!original) {
            const config = itemsConfig.get(id);
            if (!config) return { top: '50%', left: '50%' };
            return { top: config.top, left: config.left };
        }

        // Check both pendingFocusRef and state.phase
        // to ensure correct positions during transitions
        const pendingPhase = pendingFocusRef.current.phase;
        const statePhase = state.phase;

        let position: Position;
        let reason: string;

        // If idle, return original position
        if (pendingPhase === 'idle' && statePhase === 'idle') {
            position = original;
            reason = 'idle';
        }
        // If unfocusing, return original position for Framer Motion to animate back
        else if (statePhase === 'unfocusing' || pendingPhase === 'unfocusing') {
            position = original;
            reason = 'unfocusing';
        }
        // If focused or focusing, use scatter positions
        // Note: For self-focus items, focus placement is already applied to scatterPositions
        else if (pendingPhase === 'focusing' || pendingPhase === 'focused' || 
            statePhase === 'focusing' || statePhase === 'focused') {
            const scatterPos = scatterPositionsRef.current.get(id);
            if (!scatterPos) {
                position = original;
                reason = 'no-scatter-pos';
            } else {
                position = scatterPos;
                reason = 'scatter';
            }
        } else {
            position = original;
            reason = 'fallback';
        }

        if (process.env.NODE_ENV === 'development') {
            const isSchedule = id === TopDownItemId.Schedule;
            const logPrefix = isSchedule ? '[Pos] Schedule' : '[Pos] Other';
            console.log(logPrefix, {
                timestamp: timestamp.toFixed(2),
                itemId: id,
                pendingPhase,
                statePhase,
                reason,
                position: `${position.top}, ${position.left}`,
            });
        }

        return position;
    }, [itemsConfig, state.phase]);

    const isItemFocused = useCallback((id: TopDownItemId): boolean => {
        return state.phase === 'focused' && state.focusedId === id && state.mode === 'self';
    }, [state]);

    const isAnyFocus = useCallback((): boolean => {
        return state.phase === 'focusing' || state.phase === 'focused' || state.phase === 'unfocusing';
    }, [state.phase]);

    const canInteractWithItems = useCallback((): boolean => {
        return state.phase === 'idle' || state.phase === 'focused';
    }, [state.phase]);

    /**
     * Return the full target pose for an item (position + size/rotation).
     * - Position comes from getItemPosition() which already respects scatter/focus phases.
     * - Width/rotate use item config, except when Schedule self-focus is active
     *   where focusPlacement width/rotate overrides are applied.
     */
    const getTargetPose = useCallback((id: TopDownItemId) => {
        const baseConfig = itemsConfig.get(id);
        const pos = getItemPosition(id);
        const focusedPose = focusConfig.get(id)?.focusPlacement;
        const scatterConfigForItem = scatterConfig.get(id)?.scatterPlacement;

        const isAnyFocusPhase =
            state.phase === 'focused' || state.phase === 'focusing';
        const isSelfFocused =
            isAnyFocusPhase && state.focusedId === id;

        let top = pos.top;
        let left = pos.left;
        let width = baseConfig?.width ?? '50%';
        let rotate = baseConfig?.rotate ?? '0deg';
        let z = baseConfig?.z ?? 0;

        if (isAnyFocusPhase) {
            if (isSelfFocused && focusedPose) {
                top = focusedPose.top ?? top;
                left = focusedPose.left ?? left;
                width = focusedPose.width ?? width;
                rotate = focusedPose.rotate ?? rotate;
                z = focusedPose.z ?? z;
            } else if (scatterConfigForItem) {
                top = scatterConfigForItem.top ?? top;
                left = scatterConfigForItem.left ?? left;
                width = scatterConfigForItem.width ?? width;
                rotate = scatterConfigForItem.rotate ?? rotate;
                z = scatterConfigForItem.z ?? z;
            }
        }

        return { top, left, width, rotate, z };
    }, [itemsConfig, focusConfig, scatterConfig, state.phase, state.focusedId, getItemPosition]);

    return {
        state,
        focusItem,
        dismiss,
        getItemPosition,
        getTargetPose,
        isItemFocused,
        isAnyFocus,
        canInteractWithItems,
        onContentExitComplete,
    };
}

